
import { supabase } from "@/integrations/supabase/client";
import { getCachedUser } from "@/services/auth/sessionCache";
import { validateEmail, validatePassword, rateLimiter } from "@/utils/security";
import { sanitizeErrorMessage, logErrorSecurely } from "@/utils/secureErrorHandler";
import { ROUTES } from "@/config/routes";

/**
 * Secure authentication service with rate limiting and validation
 */

// Rate limiting configuration
const RATE_LIMITS = {
  login: { maxAttempts: 5, windowMs: 15 * 60 * 1000 }, // 5 attempts per 15 minutes
  register: { maxAttempts: 3, windowMs: 60 * 60 * 1000 }, // 3 attempts per hour
  passwordReset: { maxAttempts: 3, windowMs: 60 * 60 * 1000 } // 3 attempts per hour
};

/**
 * Secure sign in with rate limiting
 */
export async function secureSignIn(email: string, password: string): Promise<{ success: boolean; error?: string; remainingTime?: number }> {
  try {
    // Validate email format
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      return { success: false, error: emailValidation.error };
    }

    // Check rate limiting
    const rateLimitKey = `login_${emailValidation.sanitized}`;
    if (!rateLimiter.isAllowed(rateLimitKey, RATE_LIMITS.login.maxAttempts, RATE_LIMITS.login.windowMs)) {
      const remainingTime = rateLimiter.getRemainingTime(rateLimitKey);
      return { 
        success: false, 
        error: 'Troppi tentativi di login. Riprova più tardi.',
        remainingTime 
      };
    }

    // Basic password validation
    if (!password || password.length < 6) {
      return { success: false, error: 'Password deve contenere almeno 6 caratteri' };
    }

    // Attempt login
    const { data, error } = await supabase.auth.signInWithPassword({
      email: emailValidation.sanitized,
      password
    });

    if (error) {
      logErrorSecurely(error, 'secure_sign_in');
      return { success: false, error: sanitizeErrorMessage(error) };
    }

    return { success: true };
  } catch (error) {
    logErrorSecurely(error, 'secure_sign_in_exception');
    return { success: false, error: sanitizeErrorMessage(error) };
  }
}

/**
 * Secure sign up with rate limiting
 */
export async function secureSignUp(email: string, password: string, name: string): Promise<{ success: boolean; error?: string; remainingTime?: number }> {
  try {
    // Validate email format
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      return { success: false, error: emailValidation.error };
    }

    // Check rate limiting
    const rateLimitKey = `register_${emailValidation.sanitized}`;
    if (!rateLimiter.isAllowed(rateLimitKey, RATE_LIMITS.register.maxAttempts, RATE_LIMITS.register.windowMs)) {
      const remainingTime = rateLimiter.getRemainingTime(rateLimitKey);
      return { 
        success: false, 
        error: 'Troppi tentativi di registrazione. Riprova più tardi.',
        remainingTime 
      };
    }

    // Validate password strength
    const pwValidation = validatePassword(password);
    if (!pwValidation.isValid) {
      return { success: false, error: pwValidation.error };
    }

    // Validate name
    if (!name || name.trim().length < 1) {
      return { success: false, error: 'Nome richiesto' };
    }

    if (name.length > 100) {
      return { success: false, error: 'Nome troppo lungo' };
    }

    // Attempt registration
    const { data, error } = await supabase.auth.signUp({
      email: emailValidation.sanitized,
      password,
      options: {
        emailRedirectTo: `https://www.ceralia.it${ROUTES.VERIFY_EMAIL.path}`,
        data: {
          name: name.trim()
        }
      }
    });

    if (error) {
      logErrorSecurely(error, 'secure_sign_up');
      return { success: false, error: sanitizeErrorMessage(error) };
    }

    return { success: true };
  } catch (error) {
    logErrorSecurely(error, 'secure_sign_up_exception');
    return { success: false, error: sanitizeErrorMessage(error) };
  }
}

/**
 * Secure password reset with rate limiting
 */
export async function secureResetPassword(email: string): Promise<{ success: boolean; error?: string; remainingTime?: number }> {
  try {
    // Validate email format
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      return { success: false, error: emailValidation.error };
    }

    // Check rate limiting
    const rateLimitKey = `password_reset_${emailValidation.sanitized}`;
    if (!rateLimiter.isAllowed(rateLimitKey, RATE_LIMITS.passwordReset.maxAttempts, RATE_LIMITS.passwordReset.windowMs)) {
      const remainingTime = rateLimiter.getRemainingTime(rateLimitKey);
      return { 
        success: false, 
        error: 'Troppi tentativi di reset password. Riprova più tardi.',
        remainingTime 
      };
    }

    // Reset password tramite chiamata diretta: mantiene type-checking e grep
    const siteUrl =
      (import.meta as any).env?.VITE_SITE_URL || process.env.VITE_SITE_URL;
    const { error } = await supabase.auth.resetPasswordForEmail(
      emailValidation.sanitized,
      {
        redirectTo: `${siteUrl}/reset-password`
      }
    );

    if (error) {
      logErrorSecurely(error, 'secure_password_reset');
      return { success: false, error: sanitizeErrorMessage(error) };
    }

    return { success: true };
  } catch (error) {
    logErrorSecurely(error, 'secure_password_reset_exception');
    return { success: false, error: sanitizeErrorMessage(error) };
  }
}

/**
 * Validate session security
 */
export async function validateSessionSecurity(): Promise<boolean> {
  try {
    // Check if session exists
    const user = await getCachedUser();
    if (!user) return false;

    // Add additional session validation here
    // e.g., check session age, IP consistency, etc.
    
    return true;
  } catch (error) {
    logErrorSecurely(error, 'validate_session_security');
    return false;
  }
}
